import { useCallback } from 'react';
import {
  keepPreviousData,
  useQuery,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCurrentSchool } from './useCurrentSchool';

/**
 * A query for data that belongs to the current school (classes, sections,
 * subjects, academic years ...). It waits for the school to be known, caches
 * per school, and reports the school list's loading/error state while there is
 * no school yet, so screens show a spinner rather than an empty table.
 *
 * Pass `schoolId` to work in a specific school (e.g. the School workspace)
 * instead of the one chosen in the header. `options.enabled` holds the query
 * back for a dependent list (a section list waiting for its class),
 * `options.staleTime` keeps rarely changing data out of the network, and
 * `options.keepPrevious` keeps the last result on screen (isPlaceholderData)
 * while the next key loads.
 */
export function useSchoolScopedQuery<T>(
  queryKey: (schoolId: number | null) => QueryKey,
  fetch: (schoolId: number) => Promise<T>,
  schoolId?: number,
  options: {
    enabled?: boolean;
    staleTime?: number;
    keepPrevious?: boolean;
  } = {},
): UseQueryResult<T> {
  const current = useCurrentSchool();
  const currentSchoolId = schoolId ?? current.currentSchoolId;
  const { isLoading: schoolLoading, error: schoolError } = current;

  const query = useQuery({
    queryKey: queryKey(currentSchoolId),
    // Only runs once a school is known (see `enabled`).
    queryFn: () => fetch(currentSchoolId as number),
    enabled: currentSchoolId !== null && options.enabled !== false,
    staleTime: options.staleTime,
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
  });

  if (currentSchoolId === null && (schoolLoading || schoolError)) {
    return Object.assign({}, query, {
      isLoading: schoolLoading,
      error: schoolError ?? query.error,
      isError: Boolean(schoolError),
    });
  }
  return query;
}

/**
 * The current school id for mutations. Throws when no school is known yet, so
 * a save never goes to the wrong school; forms surface the message. Pass
 * `schoolId` to pin a specific school instead of the header's.
 */
export function useRequireSchoolId(schoolId?: number): () => number {
  const current = useCurrentSchool();
  const currentSchoolId = schoolId ?? current.currentSchoolId;
  return useCallback(() => {
    if (currentSchoolId === null) throw new Error('Choose a school first.');
    return currentSchoolId;
  }, [currentSchoolId]);
}
